import { Component, OnDestroy, OnInit, signal, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of } from 'rxjs';
import { catchError, defaultIfEmpty, map, switchMap } from 'rxjs/operators';
import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { ArmyBuilderConfig } from '../global/config';
import { DataLibrary } from '../global/data-library';
import { getValueFromTranslationSync } from '../global/language/selectLanguage';
import { ToastService } from '../global/toast/toast';
import { snapshot } from '../global/utils';
import { Modal } from '../global/modal/modal';
import { InputAlert } from '../global/input-alert/input-alert';
import { AlertService } from '../global/alert/alert';
import { ForceUtils } from '../forces/force.utils';
import { selectForces } from '../forces/state/selectors';

import { EventService } from './event.service';
import { ListValidationSummary, summariseValidation } from './list-validation-summary';
import { AppEvent, EventRegistration } from './models';
import { buildRegistrationGroups, RegistrationGroups, RegistrationRow, RegistrationSlotRow } from './registration-view-model';

@Component({
    standalone: false,
    selector: 'abs-event-manage-registrations',
    template: `
        @if (event$ | async; as event) {
            <abs-page-layout [pageTitle]="'GLOBAL.EVENTS.REGISTRATION.MANAGE_TITLE' | translate">
                <abs-admin-panel label="Admin: Event Testing">
                    <div class="admin-test-data">
                        <label>
                            Registrations to create
                            <input type="number" min="1" max="40" [(ngModel)]="testRegistrationCount" />
                        </label>
                        <ion-button class="btn-sm" [disabled]="generatingTestData()" (click)="generateTestRegistrations(event)">
                            Generate
                        </ion-button>
                        <ion-button
                            class="btn-sm"
                            color="danger"
                            fill="outline"
                            [disabled]="generatingTestData()"
                            (click)="removeTestRegistrations(event)"
                        >
                            Remove test registrations
                        </ion-button>
                    </div>
                </abs-admin-panel>
                @if (groups$ | async; as groups) {
                    <div class="recheck-bar">
                        <ion-button class="btn-sm" fill="outline" [disabled]="recheckInProgress()" (click)="recheckLists(event, groups)">
                            @if (recheckInProgress()) {
                                <ion-spinner slot="start"></ion-spinner>
                                <span>
                                    {{
                                        'GLOBAL.EVENTS.REGISTRATION.RECHECKING' | translate: { done: recheckDone(), total: recheckTotal() }
                                    }}
                                </span>
                            } @else {
                                <ion-icon name="refresh-outline" slot="start"></ion-icon>
                                <span>{{ 'GLOBAL.EVENTS.REGISTRATION.RECHECK_LISTS' | translate }}</span>
                            }
                        </ion-button>
                        <!-- A summary is computed on the submitting player's device, against
                             whatever app version and data they had. This is how an organiser
                             gets one computed against theirs instead. -->
                        <p class="recheck-note">{{ 'GLOBAL.EVENTS.REGISTRATION.RECHECK_LISTS_NOTE' | translate }}</p>
                    </div>

                    <ng-template
                        #groupTemplate
                        let-rows="rows"
                        let-headingKey="headingKey"
                        let-showApprove="showApprove"
                        let-showRemove="showRemove"
                        let-recipients="recipients"
                    >
                        @if (rows.length > 0) {
                            <div class="reg-group">
                                <h4 class="sub-title">{{ headingKey | translate }} ({{ rows.length }})</h4>
                                @if (recipients?.addresses?.length) {
                                    <div class="reg-recipients">
                                        <ion-button size="small" fill="outline" (click)="copyAddresses(recipients.addresses)">
                                            <ion-icon name="copy-outline" slot="start"></ion-icon>
                                            {{ 'GLOBAL.EVENTS.REGISTRATION.COPY_ADDRESSES' | translate }}
                                        </ion-button>
                                        @if (recipients.mailto) {
                                            <ion-button size="small" fill="outline" [href]="recipients.mailto">
                                                <ion-icon name="mail-outline" slot="start"></ion-icon>
                                                {{ 'GLOBAL.EVENTS.REGISTRATION.EMAIL_GROUP' | translate }}
                                            </ion-button>
                                        }
                                    </div>
                                }
                                <ion-list lines="full">
                                    @for (row of rows; track row.registration._id) {
                                        <ion-item class="reg-item" lines="full">
                                            <!--
                                                Deliberately NOT ion-label: it stops short of the
                                                slot="end" buttons, so the row's top-right corner was
                                                unusable for the date or the incomplete chip, and its
                                                h3 carries a 15px side margin that pushed the name
                                                further in than every other line. Everything lives in
                                                the item's single unslotted area instead, so this
                                                component's own CSS decides the shape -- the same
                                                approach round-management uses for a pairing.
                                            -->
                                            <div class="reg-row">
                                                <div class="reg-row-head">
                                                    <div class="reg-row-name">
                                                        {{ row.registration.displayName }}
                                                        @if (row.registration.contactEmail || row.registration.email) {
                                                            <a
                                                                class="reg-row-email"
                                                                href="mailto:{{ row.registration.contactEmail || row.registration.email }}"
                                                            >
                                                                ({{ row.registration.contactEmail || row.registration.email }})
                                                            </a>
                                                        }
                                                    </div>
                                                    <!-- The date is ranged right because every registration has
                                                         one, so it uses the corner that would otherwise be empty
                                                         on every row. The incomplete marker sits below the name
                                                         instead: only a few rows carry it, so it does not earn
                                                         permanent space in the header line. -->
                                                    @if (row.registration.registeredAt) {
                                                        <div class="reg-row-date">
                                                            {{ row.registration.registeredAt | date: 'd MMM y' }}
                                                        </div>
                                                    }
                                                </div>

                                                @if (!row.contactDetailsComplete) {
                                                    <div class="reg-row-flags">
                                                        <span class="details-missing-marker">
                                                            {{ 'GLOBAL.EVENTS.REGISTRATION.DETAILS_MISSING' | translate }}
                                                        </span>
                                                    </div>
                                                }

                                                <p class="list-progress" [class.list-progress-complete]="row.complete">
                                                    {{
                                                        'GLOBAL.EVENTS.REGISTRATION.LIST_PROGRESS'
                                                            | translate
                                                                : { submitted: row.progress.submitted, required: row.progress.required }
                                                    }}
                                                </p>

                                                @for (slot of row.slots; track slot.label) {
                                                    <div class="list-slot">
                                                        <div class="list-slot-body">
                                                            <div class="list-slot-info">
                                                                <div class="list-slot-row">
                                                                    <ion-icon
                                                                        [name]="slot.indicator.icon"
                                                                        [color]="slot.indicator.color"
                                                                    ></ion-icon>
                                                                    <span class="list-slot-label">{{ slot.label }}</span>
                                                                    @if (!slot.required) {
                                                                        <span class="list-slot-optional">
                                                                            {{ 'GLOBAL.EVENTS.REGISTRATION.OPTIONAL_SLOT' | translate }}
                                                                        </span>
                                                                    }
                                                                    <span class="list-slot-state">{{
                                                                        slot.indicator.labelKey | translate
                                                                    }}</span>
                                                                    @if (slot.resubmitted) {
                                                                        <!-- A resubmission is otherwise indistinguishable from a
                                                                     first submission, so the organiser who asked for the
                                                                     change has nothing telling them it arrived. -->
                                                                        <span class="list-slot-resubmitted">
                                                                            {{ 'GLOBAL.EVENTS.REGISTRATION.RESUBMITTED' | translate }}
                                                                        </span>
                                                                    }
                                                                </div>
                                                                @if (slot.present) {
                                                                    <div class="list-slot-detail">
                                                                        <div class="list-slot-name">
                                                                            {{
                                                                                slot.listName ||
                                                                                    ('GLOBAL.EVENTS.REGISTRATION.UNNAMED_LIST' | translate)
                                                                            }}
                                                                            @if (slot.listPoints != null) {
                                                                                <span class="list-slot-points"
                                                                                    >({{ slot.listPoints }} pts)</span
                                                                                >
                                                                            }
                                                                        </div>
                                                                        <!-- A text list carries no snapshot, so the faction does not
                                                                     exist for it; shown as unknown rather than left blank. -->
                                                                        <div class="list-slot-faction">
                                                                            {{
                                                                                (factionNames$ | async)?.[slot.listFaction ?? ''] ||
                                                                                    slot.listFaction ||
                                                                                    ('GLOBAL.EVENTS.REGISTRATION.UNKNOWN_FACTION'
                                                                                        | translate)
                                                                            }}
                                                                        </div>
                                                                    </div>
                                                                }
                                                                @if (slot.organiserNotes) {
                                                                    <!-- Outside the list-details block on purpose: a slot accepted
                                                                 with no list is exactly the case where an organiser's
                                                                 reasoning matters to a co-organiser. -->
                                                                    <div class="list-slot-notes">
                                                                        {{ 'GLOBAL.EVENTS.REGISTRATION.ORGANISER_NOTE' | translate }}:
                                                                        {{ slot.organiserNotes }}
                                                                    </div>
                                                                }
                                                                @if (slot.decidedAt) {
                                                                    <div class="list-slot-decision">
                                                                        @if (slot.decidedAutomatically) {
                                                                            <!-- No name: the event's rule accepted it, not a
                                                                         person. Crediting whoever is looking would be
                                                                         a lie an organiser might later rely on. -->
                                                                            {{
                                                                                'GLOBAL.EVENTS.REGISTRATION.ACCEPTED_AUTOMATICALLY'
                                                                                    | translate
                                                                                        : { date: slot.decidedAt | date: 'd MMM y, HH:mm' }
                                                                            }}
                                                                        } @else {
                                                                            {{
                                                                                (slot.accepted
                                                                                    ? 'GLOBAL.EVENTS.REGISTRATION.ACCEPTED_BY'
                                                                                    : 'GLOBAL.EVENTS.REGISTRATION.REJECTED_BY'
                                                                                )
                                                                                    | translate
                                                                                        : {
                                                                                              name: organiserName(
                                                                                                  event,
                                                                                                  slot.decidedByUserId
                                                                                              ),
                                                                                              date: slot.decidedAt | date: 'd MMM y, HH:mm'
                                                                                          }
                                                                            }}
                                                                        }
                                                                    </div>
                                                                }
                                                            </div>
                                                            <!-- Ranged right, beside the details rather than beneath them.
                                                             btn-xs keeps three labelled buttons compact enough to sit
                                                             on the same line on a phone. -->
                                                            <div class="list-slot-actions">
                                                                @if (slot.viewableForceId) {
                                                                    <ion-button
                                                                        class="btn-xs"
                                                                        fill="outline"
                                                                        [routerLink]="[
                                                                            event.gameId,
                                                                            'forces',
                                                                            'shared',
                                                                            slot.viewableForceId
                                                                        ]"
                                                                        [relativeTo]="null"
                                                                    >
                                                                        {{ 'GLOBAL.EVENTS.REGISTRATION.VIEW_LIST' | translate }}
                                                                    </ion-button>
                                                                } @else if (slot.viewableText) {
                                                                    <ion-button
                                                                        class="btn-xs"
                                                                        fill="outline"
                                                                        (click)="viewText(slot.viewableText)"
                                                                    >
                                                                        {{ 'GLOBAL.EVENTS.REGISTRATION.VIEW_LIST' | translate }}
                                                                    </ion-button>
                                                                }
                                                                @if (!slot.accepted) {
                                                                    <ion-button
                                                                        class="btn-xs"
                                                                        color="success"
                                                                        (click)="acceptList(event, row.registration, slot)"
                                                                    >
                                                                        {{ 'GLOBAL.EVENTS.REGISTRATION.ACCEPT_LIST' | translate }}
                                                                    </ion-button>
                                                                }
                                                                @if (slot.present) {
                                                                    <ion-button
                                                                        class="btn-xs"
                                                                        fill="outline"
                                                                        (click)="rejectList(event, row.registration, slot)"
                                                                    >
                                                                        {{ 'GLOBAL.EVENTS.REGISTRATION.REJECT_LIST' | translate }}
                                                                    </ion-button>
                                                                }
                                                            </div>
                                                        </div>
                                                    </div>
                                                }

                                                @if (showApprove || showRemove) {
                                                    <div class="reg-row-actions">
                                                        @if (showApprove) {
                                                            <ion-button
                                                                class="btn-sm"
                                                                color="success"
                                                                (click)="approveRegistration(event, row.registration)"
                                                            >
                                                                {{ 'GLOBAL.EVENTS.REGISTRATION.APPROVE' | translate }}
                                                            </ion-button>
                                                        }
                                                        @if (showRemove) {
                                                            <ion-button
                                                                class="btn-sm"
                                                                fill="outline"
                                                                color="danger"
                                                                (click)="removeRegistration(event, row.registration)"
                                                            >
                                                                {{ 'GLOBAL.EVENTS.REGISTRATION.REMOVE_PLAYER' | translate }}
                                                            </ion-button>
                                                        }
                                                    </div>
                                                }
                                            </div>
                                        </ion-item>
                                    }
                                </ion-list>
                            </div>
                        }
                    </ng-template>

                    @if (groups.recipients.everyone.addresses.length) {
                        <div class="reg-recipients reg-recipients-all">
                            <ion-button size="small" (click)="copyAddresses(groups.recipients.everyone.addresses)">
                                <ion-icon name="copy-outline" slot="start"></ion-icon>
                                {{
                                    'GLOBAL.EVENTS.REGISTRATION.COPY_ALL_ADDRESSES'
                                        | translate: { count: groups.recipients.everyone.addresses.length }
                                }}
                            </ion-button>
                            @if (groups.recipients.everyone.mailto) {
                                <ion-button size="small" fill="outline" [href]="groups.recipients.everyone.mailto">
                                    <ion-icon name="mail-outline" slot="start"></ion-icon>
                                    {{ 'GLOBAL.EVENTS.REGISTRATION.EMAIL_EVERYONE' | translate }}
                                </ion-button>
                            }
                        </div>
                        <p class="reg-recipients-note">{{ 'GLOBAL.EVENTS.REGISTRATION.BCC_NOTE' | translate }}</p>
                    }

                    <ng-container
                        *ngTemplateOutlet="
                            groupTemplate;
                            context: {
                                rows: groups.pending,
                                recipients: groups.recipients.pending,
                                headingKey: 'GLOBAL.EVENTS.REGISTRATION.PENDING',
                                showApprove: true,
                                showRemove: true
                            }
                        "
                    ></ng-container>
                    <ng-container
                        *ngTemplateOutlet="
                            groupTemplate;
                            context: {
                                rows: groups.active,
                                recipients: groups.recipients.active,
                                headingKey: 'GLOBAL.EVENTS.REGISTRATION.ACTIVE',
                                showApprove: false,
                                showRemove: true
                            }
                        "
                    ></ng-container>
                    <ng-container
                        *ngTemplateOutlet="
                            groupTemplate;
                            context: {
                                rows: groups.waitlist,
                                recipients: groups.recipients.waitlist,
                                headingKey: 'GLOBAL.EVENTS.REGISTRATION.WAITLISTED',
                                showApprove: true,
                                showRemove: false
                            }
                        "
                    ></ng-container>

                    @if (groups.pending.length + groups.active.length + groups.waitlist.length === 0) {
                        <div class="empty-state">
                            <ion-icon name="people-outline"></ion-icon>
                            <p>{{ 'GLOBAL.EVENTS.REGISTRATION.NO_REGISTRATIONS' | translate }}</p>
                        </div>
                    }
                }
            </abs-page-layout>
        }

        <ng-template #textSubmissionModal let-data="data">
            <p class="text-submission-body">{{ data.text }}</p>
        </ng-template>
    `
})
export class EventManageRegistrationsPage implements OnInit, OnDestroy {
    private eventService = inject(EventService);
    private route = inject(ActivatedRoute);
    private forceUtils = inject(ForceUtils);
    private dataLibrary = inject(DataLibrary);
    private config = inject(ArmyBuilderConfig);
    private toastService = inject(ToastService);
    private translateService = inject(TranslateService);
    private store = inject(Store);
    private modal = inject(Modal);
    private inputAlert = inject(InputAlert);
    private alertService = inject(AlertService);

    @ViewChild('textSubmissionModal') textSubmissionModal!: TemplateRef<any>;

    event$: Observable<AppEvent>;
    registrations$: Observable<EventRegistration[]>;
    groups$: Observable<RegistrationGroups>;

    recheckInProgress = signal(false);
    recheckDone = signal(0);
    recheckTotal = signal(0);

    testRegistrationCount = 8;
    generatingTestData = signal(false);

    private refreshRegistrations$ = new BehaviorSubject<void>(undefined);
    private destroyed = false;

    ngOnInit() {
        this.event$ = this.route.paramMap.pipe(
            map((params) => params.get('eventId')),
            switchMap((id) => this.eventService.getEvent(id))
        );

        this.registrations$ = combineLatest([this.event$, this.refreshRegistrations$]).pipe(
            switchMap(([event]) => {
                if (!event?._id) return of([]);
                return this.eventService.listRegistrations(event._id).pipe(catchError(() => of([])));
            })
        );

        this.groups$ = combineLatest([this.registrations$, this.event$]).pipe(
            map(([registrations, event]) => buildRegistrationGroups(registrations, event))
        );
    }

    ngOnDestroy() {
        this.destroyed = true;
    }

    approveRegistration(event: AppEvent, reg: EventRegistration) {
        this.eventService.updateRegistrationStatus(event._id, reg._id, 'active').subscribe(() => {
            this.refreshRegistrations$.next();
        });
    }

    // A required reason, not optional: a removal looks identical to a bug from the registrant's
    // side unless they're told why (see the API-side comment on updateRegistrationStatus).
    async removeRegistration(event: AppEvent, reg: EventRegistration) {
        // Confirm before the reason prompt: the prompt asks what to tell the player, which
        // reads as a form rather than as "this removes them from the event".
        const confirmed = await this.alertService.showConfirm(
            this.translateService.instant('GLOBAL.EVENTS.REGISTRATION.REMOVE_PLAYER_CONFIRM_TITLE'),
            this.translateService.instant('GLOBAL.EVENTS.REGISTRATION.REMOVE_PLAYER_CONFIRM_PROMPT', {
                name: reg.displayName || reg.contactEmail || reg.email
            }),
            {
                ok: this.translateService.instant('GLOBAL.EVENTS.REGISTRATION.REMOVE'),
                cancel: this.translateService.instant('GLOBAL.CANCEL')
            }
        );
        if (confirmed.role !== 'ok') return;

        let notes: string;
        try {
            notes = await this.inputAlert.show(
                this.translateService.instant('GLOBAL.EVENTS.REGISTRATION.REMOVE_PLAYER'),
                this.translateService.instant('GLOBAL.EVENTS.REGISTRATION.REMOVE_PLAYER_PROMPT'),
                '',
                'textarea',
                true
            );
        } catch {
            return;
        }

        this.eventService.updateRegistrationStatus(event._id, reg._id, 'removed', notes).subscribe(() => {
            this.refreshRegistrations$.next();
        });
    }

    /**
     * Signs a list off. Optional, so its absence still means "not yet looked at" rather
     * than "rejected" -- the point is the positive record, which matters most for a list
     * carrying validation errors, a free-text list, or an event with several organisers.
     */
    /** Faction key (`BRIT_COM`) -> display name. This page is organiser-only and sits under
     *  a game route, so the library is normally present. */
    readonly factionNames$: Observable<Record<string, string>> = (this.dataLibrary?.factions$ ?? of([])).pipe(
        map((factions) =>
            factions.reduce(
                (agg, faction) => ({ ...agg, [faction.key]: getValueFromTranslationSync(faction.name) }),
                {} as Record<string, string>
            )
        ),
        defaultIfEmpty({} as Record<string, string>)
    );

    /**
     * Organiser id -> display name, from the event's own organiser details. Every list
     * decision is made by an organiser or co-organiser, so this needs no user lookup.
     */
    organiserName(event: AppEvent, userId: string | undefined): string {
        if (!userId) return '';
        if (event.organiserId === userId) {
            return event.organiserDisplayName || userId;
        }
        const co = (event.coOrganiserDetails ?? []).find((c) => c.userId === userId);
        return co?.displayName || userId;
    }

    async acceptList(event: AppEvent, registration: EventRegistration, slot: RegistrationSlotRow) {
        // Accepting an empty slot is legitimate but has a consequence worth stating: nobody
        // will be able to view a list that does not exist.
        if (!slot.present) {
            const confirmed = await this.alertService.showConfirm(
                this.translateService.instant('GLOBAL.EVENTS.REGISTRATION.ACCEPT_NO_LIST_TITLE'),
                this.translateService.instant('GLOBAL.EVENTS.REGISTRATION.ACCEPT_NO_LIST_PROMPT'),
                {
                    ok: this.translateService.instant('GLOBAL.EVENTS.REGISTRATION.ACCEPT_LIST'),
                    cancel: this.translateService.instant('GLOBAL.CANCEL')
                }
            );
            if (confirmed.role !== 'ok') return;
        }

        let notes: string;
        try {
            // Only mention validation errors when there are some. A text list has nothing to
            // validate, so offering that as the example reason made no sense for one.
            const promptKey = slot.hasValidationErrors
                ? 'GLOBAL.EVENTS.REGISTRATION.ACCEPT_LIST_PROMPT_ERRORS'
                : 'GLOBAL.EVENTS.REGISTRATION.ACCEPT_LIST_PROMPT';
            notes = await this.inputAlert.show(
                this.translateService.instant('GLOBAL.EVENTS.REGISTRATION.ACCEPT_LIST'),
                this.translateService.instant(promptKey),
                '',
                'textarea',
                false
            );
        } catch {
            return;
        }

        this.eventService
            .acceptListSubmission(event._id, registration._id, { slotLabel: slot.label, slotId: slot.slotId, notes })
            .subscribe({
                next: () => {
                    this.refreshRegistrations$.next();
                    this.toastService.showToast('', this.translateService.instant('GLOBAL.EVENTS.REGISTRATION.ACCEPT_LIST_DONE'), {
                        color: 'success',
                        duration: 3000
                    });
                },
                error: (err) => {
                    const message = err?.error?.message || this.translateService.instant('GLOBAL.EVENTS.REGISTRATION.ACCEPT_LIST_FAILED');
                    this.toastService.showToast('', message, { color: 'danger', duration: 4000 });
                }
            });
    }

    // Sends the list back to the registrant for revision rather than removing them from the
    // event — the force unlocks (see rejectListSubmission's server-side comment) and the
    // registrant's own page already shows the resubmit button and this note for a 'rejected' slot.
    async rejectList(event: AppEvent, registration: EventRegistration, slot: RegistrationSlotRow) {
        let notes: string;
        try {
            notes = await this.inputAlert.show(
                this.translateService.instant('GLOBAL.EVENTS.REGISTRATION.REJECT_LIST'),
                this.translateService.instant('GLOBAL.EVENTS.REGISTRATION.REJECT_LIST_PROMPT'),
                '',
                'textarea',
                false
            );
        } catch {
            return;
        }

        this.eventService
            .rejectListSubmission(event._id, registration._id, { slotLabel: slot.label, slotId: slot.slotId, notes })
            .subscribe({
                next: () => {
                    this.refreshRegistrations$.next();
                    this.toastService.showToast('', this.translateService.instant('GLOBAL.EVENTS.REGISTRATION.REJECT_LIST_DONE'), {
                        color: 'success',
                        duration: 3000
                    });
                },
                error: () => {
                    this.toastService.showToast('', this.translateService.instant('GLOBAL.EVENTS.REGISTRATION.REJECT_LIST_FAILED'), {
                        color: 'danger',
                        duration: 3000
                    });
                }
            });
    }

    viewText(text: string) {
        this.modal.show({
            props: {
                title: this.translateService.instant('GLOBAL.EVENTS.REGISTRATION.VIEW_LIST'),
                contentTemplate: this.textSubmissionModal,
                data: { text }
            }
        });
    }

    /**
     * Puts the addresses on the clipboard for pasting into BCC. Deliberately not To or CC: the
     * organiser is the only person entitled to these addresses, and a visible recipient list would
     * hand every player everyone else's.
     */
    async copyAddresses(addresses: string[]) {
        await navigator.clipboard.writeText(addresses.join(', '));
        this.toastService.showToast(
            '',
            this.translateService.instant('GLOBAL.EVENTS.REGISTRATION.ADDRESSES_COPIED', { count: addresses.length }),
            { color: 'success', duration: 3000 }
        );
    }

    async recheckLists(event: AppEvent, groups: RegistrationGroups) {
        const targets: { registrationId: string; slotId?: number; slotLabel: string; forceId: string }[] = [];
        for (const row of [...groups.pending, ...groups.active, ...groups.waitlist]) {
            for (const slot of row.slots) {
                if (slot.viewableForceId) {
                    targets.push({
                        registrationId: row.registration._id,
                        slotId: slot.slotId,
                        slotLabel: slot.label,
                        forceId: slot.viewableForceId
                    });
                }
            }
        }

        if (targets.length === 0) {
            return;
        }

        this.recheckTotal.set(targets.length);
        this.recheckDone.set(0);
        this.recheckInProgress.set(true);

        let failed = 0;
        const summaryCache = new Map<string, ListValidationSummary>();
        // Sequential on purpose: hydrating a whole event's worth of forces in parallel is exactly
        // the kind of burst the entity-preload batching notes in CLAUDE.md exist to prevent.
        for (const target of targets) {
            if (this.destroyed) {
                return;
            }
            try {
                let summary = summaryCache.get(target.forceId);
                if (!summary) {
                    summary = await this.validateForceById(target.forceId);
                    summaryCache.set(target.forceId, summary);
                }
                await firstValueFrom(
                    this.eventService.setListValidation(event._id, target.registrationId, {
                        slotLabel: target.slotLabel,
                        slotId: target.slotId,
                        validationSummary: summary
                    })
                );
            } catch {
                failed++;
            }
            this.recheckDone.update((n) => n + 1);
        }

        if (this.destroyed) {
            return;
        }

        this.recheckInProgress.set(false);
        this.refreshRegistrations$.next();
        this.toastService.showToast(
            '',
            failed > 0
                ? this.translateService.instant('GLOBAL.EVENTS.REGISTRATION.RECHECK_FAILED')
                : this.translateService.instant('GLOBAL.EVENTS.REGISTRATION.RECHECK_DONE', { total: targets.length }),
            { color: failed > 0 ? 'warning' : 'success', duration: 3000 }
        );
    }

    private async validateForceById(forceId: string): Promise<ListValidationSummary> {
        const unitTemplates = await firstValueFrom(this.dataLibrary.unitTemplates$);
        const raw = await firstValueFrom(this.forceUtils.getForceFromServer(forceId));
        const preProcessed = this.forceUtils.preProcessForce(raw, unitTemplates, raw.id);
        const processed = await this.forceUtils.processForce(preProcessed);
        const validated = this.forceUtils.postProcessForce(processed);
        return summariseValidation(validated.validationErrors, this.config.version);
    }

    generateTestRegistrations(event: AppEvent) {
        this.generatingTestData.set(true);
        // Submissions point at the admin's own forces for this game, so every generated list is a
        // real, openable force and nothing fake is written into force storage.
        //
        // ForceService has no public forces observable -- it reads the store directly (see
        // force.service.ts:373) -- so do the same here. selectForces is not on the ABS barrel, but
        // this page lives inside ABS so the relative import is fine.
        snapshot(this.store.select(selectForces), (forces) => {
            const forceIds = (forces ?? []).filter((f) => f.gameId === event.gameId).map((f) => f.id);
            if (!forceIds.length) {
                this.generatingTestData.set(false);
                this.toastService.showToast('', 'You need at least one force in this game system first', {
                    color: 'warning',
                    duration: 3000
                });
                return;
            }
            this.eventService.generateTestRegistrations(event._id, this.testRegistrationCount, forceIds.slice(0, 5)).subscribe({
                next: (res) => {
                    this.generatingTestData.set(false);
                    this.refreshRegistrations$.next();
                    this.toastService.showToast('', `Created ${res.created} test registrations`, {
                        color: 'success',
                        duration: 3000
                    });
                },
                error: (err) => {
                    this.generatingTestData.set(false);
                    // Surfaces the server's own reason (e.g. a schema validation failure naming
                    // the offending field) when it sent one, rather than a fixed message that
                    // hid what actually went wrong behind identical, unhelpful text.
                    const message = err?.error?.message || 'Could not generate test registrations';
                    this.toastService.showToast('', message, { color: 'danger', duration: 5000 });
                }
            });
        });
    }

    removeTestRegistrations(event: AppEvent) {
        this.generatingTestData.set(true);
        this.eventService.removeTestRegistrations(event._id).subscribe({
            next: (res) => {
                this.generatingTestData.set(false);
                this.refreshRegistrations$.next();
                this.toastService.showToast('', `Removed ${res.deleted} test registrations`, { color: 'success', duration: 3000 });
            },
            error: () => {
                this.generatingTestData.set(false);
                this.toastService.showToast('', 'Could not remove test registrations', { color: 'danger', duration: 3000 });
            }
        });
    }
}
